/*
 * DialUpDuo bulk savings pill row (PDP, Lane C).
 *
 * Server-rendered pills already show the correct per-unit price for the
 * initially selected variant (Liquid computes price - floor(price*pct/100),
 * matching checkout rounding, in snippets/duo-bulk-tiers.liquid). This element only
 * has two jobs after first paint:
 *
 *   1. Tapping a pill sets the PDP <quantity-input> (assets/global.js,
 *      #Quantity-{{ section.id }}) to that tier's min_qty and dispatches
 *      the input/change events it listens for, so a cart add carries the
 *      right quantity.
 *   2. Keeping per-unit prices in sync when the shopper switches variants,
 *      by hooking the SAME mechanism assets/product-info.js already uses:
 *      PUB_SUB_EVENTS.variantChange (assets/pubsub.js + constants.js, both
 *      loaded defer in <head> before this deferred script runs). product-
 *      info.js publishes that event with the fresh variant object AFTER it
 *      swaps in the new price DOM, so we recompute straight from
 *      event.data.variant.price (already in cents, same convention as
 *      Liquid's variant.price) instead of re-parsing rendered HTML.
 *
 * Money formatting mirrors cart-drawer.js's formatMoney (same algorithm:
 * parse the {{ amount }} token out of shop.money_format). Kept as an
 * independent copy here since Lane C owns no shared/cart-drawer files.
 */
if (!customElements.get('duo-bulk-tiers')) {
  customElements.define(
    'duo-bulk-tiers',
    class DuoBulkTiers extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '';
        this.quantityInputId = this.dataset.quantityInputId || '';
        this.pills = Array.from(this.querySelectorAll('.duo-bulk-tiers__pill'));

        // Cache the burst-particle SVG shapes (hand-authored once in the
        // Liquid as inert <template>s) so the click choreography can clone
        // them without hardcoding path data here.
        this.particleShapes = {};
        this.querySelectorAll('template[data-duo-bt-particle]').forEach((tpl) => {
          this.particleShapes[tpl.dataset.duoBtParticle] = tpl.innerHTML;
        });

        this.pills.forEach((pill) => pill.addEventListener('click', () => this.onPillClick(pill)));

        this.quantityInput = this.getQuantityInput();
        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', () => this.syncActivePill());
        }
        this.syncActivePill();
        this.updateKeyAvailability();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          if (PUB_SUB_EVENTS.variantChange) {
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
          }
          // product-info.js rewrites the quantity input value programmatically
          // on a variant swap (clamping it to the new variant's bounds) WITHOUT
          // firing a DOM 'change' event, then publishes quantityUpdate. Resync
          // the active key off that so the highlight never lies about the input.
          if (PUB_SUB_EVENTS.quantityUpdate) {
            this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
              this.syncActivePill();
              this.updateKeyAvailability();
            });
          }
        }
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
      }

      getQuantityInput() {
        if (!this.quantityInputId) return null;
        return document.getElementById(this.quantityInputId);
      }

      // Re-resolve the input fresh by id every time (the theme may replace the
      // input node), caching the latest and only falling back to the cached
      // reference if the id lookup fails.
      resolveInput() {
        const fresh = this.getQuantityInput();
        if (fresh) {
          this.quantityInput = fresh;
          return fresh;
        }
        return this.quantityInput || null;
      }

      onPillClick(pill) {
        const input = this.resolveInput();
        const minQty = parseInt(pill.dataset.minQty, 10);
        if (!input || !minQty || Number.isNaN(minQty)) return;
        if (pill.getAttribute('aria-disabled') === 'true') return;

        const wasActive = pill.classList.contains('is-active');
        input.value = this.clampQty(input, minQty);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.syncActivePill();

        // Fire the power-up choreography once per fresh selection: only when
        // this tap made the key newly active (re-tapping the active key, or
        // clicking a shallower key that the quantity already satisfies,
        // stays quiet).
        if (!wasActive && pill.classList.contains('is-active')) this.playTierFx(pill);
      }

      // Clamp a tier's min-qty into the quantity input's live min/max/step,
      // mirroring product-info.js setQuantityBoundries: bound first, then snap
      // DOWN onto the step grid anchored at min. Keys must never write a
      // quantity /cart/add would cap or reject (low stock, step rules).
      clampQty(input, desired) {
        const min = parseInt(input.min, 10) || 1;
        const max = input.max ? parseInt(input.max, 10) : NaN;
        const step = parseInt(input.step, 10) || 1;
        let next = Math.max(min, desired);
        if (!Number.isNaN(max)) next = Math.min(next, max);
        if (step > 1 && next > min) next = min + Math.floor((next - min) / step) * step;
        return next;
      }

      // A key whose min-qty exceeds the input's max (available stock for the
      // selected variant) can never have its tier honored: keep it visible so
      // the ladder reads complete, but stamp aria-disabled (CSS mutes it,
      // onPillClick ignores it). Re-run whenever the bounds may have moved:
      // connect, quantityUpdate (product-info re-bounds the input on variant
      // swaps and cart fetches, then publishes it), and variantChange.
      updateKeyAvailability() {
        const input = this.resolveInput();
        if (!input) return;
        const max = input.max ? parseInt(input.max, 10) : NaN;
        this.pills.forEach((pill) => {
          const minQty = parseInt(pill.dataset.minQty, 10);
          const unreachable = !Number.isNaN(max) && !Number.isNaN(minQty) && minQty > max;
          pill.setAttribute('aria-disabled', unreachable ? 'true' : 'false');
        });
      }

      prefersReducedMotion() {
        return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      // Adds a one-shot animation class to the surface and self-removes it on
      // animationend so the same key can replay on a later selection.
      runSurfaceAnim(surface, cls) {
        surface.classList.remove('is-fx-press', 'is-fx-pop', 'is-fx-shake');
        // force reflow so re-adding a class the surface just had restarts it
        void surface.offsetWidth;
        surface.classList.add(cls);
        const done = (event) => {
          if (event.target !== surface) return;
          surface.classList.remove(cls);
          surface.removeEventListener('animationend', done);
        };
        surface.addEventListener('animationend', done);
      }

      // Builds a particle node from a cached SVG shape (or a bare span for
      // pure-CSS particles like the ring/glow), appends it to the fx layer,
      // and removes it on animationend so nothing accumulates in the DOM.
      spawnParticle(fx, className, shapeKey, style) {
        const node = document.createElement('span');
        node.className = className;
        node.setAttribute('aria-hidden', 'true');
        if (shapeKey && this.particleShapes[shapeKey]) node.innerHTML = this.particleShapes[shapeKey];
        if (style) Object.assign(node.style, style);
        const done = () => {
          node.removeEventListener('animationend', done);
          node.remove();
        };
        node.addEventListener('animationend', done);
        fx.appendChild(node);
        return node;
      }

      playTierFx(pill) {
        if (this.prefersReducedMotion()) return;
        const surface = pill.querySelector('.duo-bulk-tiers__surface');
        const fx = pill.querySelector('.duo-bulk-tiers__fx');
        if (!surface || !fx) return;

        if (pill.hasAttribute('data-tier-top')) {
          this.fxTop(surface, fx);
          return;
        }
        const index = parseInt(pill.dataset.tierIndex, 10) || 0;
        if (index <= 0) {
          // Buy 1: a quick settle press, no particles.
          this.runSurfaceAnim(surface, 'is-fx-press');
        } else if (index === 1) {
          // Tier 1: pop + two sparkles rising in tier color.
          this.runSurfaceAnim(surface, 'is-fx-pop');
          for (let i = 0; i < 2; i += 1) {
            this.spawnParticle(fx, 'bt-particle bt-particle--sparkle', 'sparkle', {
              left: 35 + i * 24 + '%',
              animationDelay: i * 60 + 'ms'
            });
          }
        } else {
          // Tier 2: pop + a ring pulse + one double-flashing zap bolt.
          this.runSurfaceAnim(surface, 'is-fx-pop');
          this.spawnParticle(fx, 'bt-ring');
          this.spawnParticle(fx, 'bt-particle bt-particle--zap', 'zap');
        }
      }

      // Top tier: shake, THEN a flame burst (flames + embers + glow bloom).
      // The persistent flicker/hover flames live in the Liquid-rendered fx
      // and are driven purely by CSS state, so nothing to add here for those.
      fxTop(surface, fx) {
        this.runSurfaceAnim(surface, 'is-fx-shake');
        const afterShake = (event) => {
          if (event.target !== surface || event.animationName !== 'bt-shake') return;
          surface.removeEventListener('animationend', afterShake);
          this.fxFlameBurst(fx);
        };
        surface.addEventListener('animationend', afterShake);
      }

      fxFlameBurst(fx) {
        this.spawnParticle(fx, 'bt-glow');
        for (let i = 0; i < 4; i += 1) {
          this.spawnParticle(fx, 'bt-particle bt-particle--flame', 'flame', {
            left: 18 + i * 21 + '%',
            animationDelay: i * 55 + 'ms'
          });
        }
        for (let i = 0; i < 3; i += 1) {
          this.spawnParticle(fx, 'bt-particle bt-particle--ember', 'ember', {
            left: 28 + i * 22 + '%',
            animationDelay: 80 + i * 70 + 'ms'
          });
        }
      }

      syncActivePill() {
        const input = this.resolveInput();
        if (!input) return;
        const qty = parseInt(input.value, 10);
        if (!qty || Number.isNaN(qty)) return;

        // Highlight the deepest tier the current quantity satisfies (same
        // "best applicable break" semantics as the automatic discounts).
        let activePill = null;
        this.pills.forEach((pill) => {
          const minQty = parseInt(pill.dataset.minQty, 10);
          if (Number.isNaN(minQty) || qty < minQty) return;
          if (!activePill || minQty > parseInt(activePill.dataset.minQty, 10)) activePill = pill;
        });

        this.pills.forEach((pill) => {
          const isActive = pill === activePill;
          pill.classList.toggle('is-active', isActive);
          pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      onVariantChange(event) {
        const sectionId = event && event.data && event.data.sectionId;
        if (sectionId && this.dataset.sectionId && sectionId !== this.dataset.sectionId) return;
        const variant = event && event.data && event.data.variant;
        if (!variant || typeof variant.price !== 'number') return;

        this.pills.forEach((pill) => {
          const priceEl = pill.querySelector('[data-duo-bt-money]');
          if (!priceEl) return;
          const percent = parseInt(pill.dataset.percent, 10) || 0;
          // Match checkout rounding: Shopify floors the per-unit DISCOUNT,
          // so the charged unit price is price - floor(price * pct / 100).
          const unitCents = variant.price - Math.floor((variant.price * percent) / 100);
          priceEl.textContent = this.formatMoney(unitCents);
        });
        this.updateKeyAvailability();
      }

      formatMoney(cents) {
        const amount = (Number(cents) || 0) / 100;
        const format = this.moneyFormat;
        const withDelimiters = (value, thousands, decimal, precision) => {
          const fixed = value.toFixed(precision);
          const parts = fixed.split('.');
          const grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts[1] ? `${grouped}${decimal}${parts[1]}` : grouped;
        };
        const token = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (token) {
          let value;
          switch (token[1]) {
            case 'amount_no_decimals':
              value = withDelimiters(amount, ',', '.', 0);
              break;
            case 'amount_with_comma_separator':
              value = withDelimiters(amount, '.', ',', 2);
              break;
            case 'amount_no_decimals_with_comma_separator':
              value = withDelimiters(amount, '.', ',', 0);
              break;
            default:
              value = withDelimiters(amount, ',', '.', 2);
          }
          return format.replace(/\{\{\s*\w+\s*\}\}/, value).replace(/<[^>]*>/g, '').trim();
        }
        try {
          return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(amount);
        } catch (error) {
          return `$${amount.toFixed(2)}`;
        }
      }
    }
  );
}
